import apiClient from '../../shared/services/apiClient';

/**
 * Host Shared 管理端补充服务
 *
 * 补齐 shared/services/hostSharedService 尚未暴露的 admin 专用端点：
 * - 平台聚合统计       GET  /api/admin/host_shared/stats
 * - 节点详情/实时统计  GET  /api/admin/host_shared/nodes/{id}(/stats)
 * - 虚拟主机操作日志    GET  /api/admin/host_shared/vhosts/{id}/logs
 * - 虚拟主机备份       GET/POST /api/admin/host_shared/vhosts/{id}/backups
 */
class HostSharedAdminService {
    /**
     * 平台聚合统计（磁盘/带宽用量、域名/数据库总数等）
     */
    async getStats() {
        try {
            const response = await apiClient.get('/api/admin/host_shared/stats');
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 节点详情
     */
    async getNode(id) {
        try {
            const response = await apiClient.get(`/api/admin/host_shared/nodes/${id}`);
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 节点实时统计（容量、虚拟主机分布、使用率趋势）
     */
    async getNodeStats(id) {
        try {
            const response = await apiClient.get(`/api/admin/host_shared/nodes/${id}/stats`);
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 虚拟主机操作日志
     */
    async getVhostLogs(id) {
        try {
            const response = await apiClient.get(`/api/admin/host_shared/vhosts/${id}/logs`);
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 虚拟主机备份列表
     */
    async getVhostBackups(id) {
        try {
            const response = await apiClient.get(`/api/admin/host_shared/vhosts/${id}/backups`);
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /**
     * 创建虚拟主机备份
     * @param {number} id vhost id
     * @param {{type:'web'|'full'|'db', backup_logs?:boolean, password?:string, db_password?:string}} data
     */
    async createVhostBackup(id, data) {
        try {
            const response = await apiClient.post(`/api/admin/host_shared/vhosts/${id}/backups`, data);
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /** 待开通订单（已付款、资源不足） */
    async getPending(status = 'pending') {
        try {
            const response = await apiClient.get('/api/admin/host_shared/pending', { params: { status } });
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /** 立即重试；nodeId = 指定节点开通（仍做安全检查） */
    async retryPending(subId, nodeId) {
        try {
            const response = await apiClient.post(`/api/admin/host_shared/pending/${subId}/retry`, nodeId ? { node_id: nodeId } : {});
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /** 取消并退款（仅人工） */
    async cancelPending(subId) {
        try {
            const response = await apiClient.post(`/api/admin/host_shared/pending/${subId}/cancel`);
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.error || error.message };
        }
    }

    /** 异地备份(R2)设置 {enabled, keep, max_keep, stats} */
    async getOffsiteSettings() {
        try {
            const response = await apiClient.get('/api/admin/host_shared/offsite-backup/settings');
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.message || error.response?.data?.error || error.message };
        }
    }

    async saveOffsiteSettings(data) {
        try {
            const response = await apiClient.put('/api/admin/host_shared/offsite-backup/settings', data);
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.message || error.response?.data?.error || error.message };
        }
    }

    /** 某站点的异地备份 {enabled, keep, backups[], restore, failed[]} */
    async getVhostOffsiteBackups(id) {
        try {
            const response = await apiClient.get(`/api/admin/host_shared/vhosts/${id}/offsite-backups`);
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.message || error.response?.data?.error || error.message };
        }
    }

    /** 立即备份一次 */
    async runVhostOffsiteBackup(id) {
        try {
            const response = await apiClient.post(`/api/admin/host_shared/vhosts/${id}/offsite-backups/run`);
            return response.data;
        } catch (error) {
            return { success: false, error: error.response?.data?.message || error.response?.data?.error || error.message };
        }
    }
}

const hostSharedAdminService = new HostSharedAdminService();
export default hostSharedAdminService;
